import React from 'react';
import { NpsCampaignDto } from '@area-restrita/api-client/model/npsCampaignDto';
import { useGetNpsCampaignResultsSummary } from '@area-restrita/api-client/endpoints/nps/nps.gen';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { isForbiddenError } from '@/modules/nps/utils';

import NpsResultsComments from './NpsResultsComments';
import NpsResultsSegmentsTabs from './NpsResultsSegmentsTabs';
import NpsResultsSummary from './NpsResultsSummary';

interface IProps {
  campaign: NpsCampaignDto;
  canViewComments: boolean;
  showComments?: boolean;
}

const NpsCampaignResultsPanel: React.FC<IProps> = ({
  campaign,
  canViewComments,
  showComments = true,
}) => {
  const summaryQuery = useGetNpsCampaignResultsSummary(campaign.id);

  if (summaryQuery.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-20 w-full" />
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-28 w-full" />
          ))}
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isForbiddenError(summaryQuery.error)) {
    return (
      <Alert>
        <AlertTitle>Resultados não disponíveis</AlertTitle>
        <AlertDescription>
          Os resultados desta campanha não estão disponíveis para o seu perfil.
        </AlertDescription>
      </Alert>
    );
  }

  if (summaryQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Não foi possível carregar os resultados</AlertTitle>
        <AlertDescription>Tente novamente.</AlertDescription>
      </Alert>
    );
  }

  if (!summaryQuery.data || summaryQuery.data.responseCount === 0) {
    return (
      <Alert>
        <AlertTitle>
          {campaign.status === 'open'
            ? 'Nenhuma resposta recebida'
            : 'Campanha sem respostas'}
        </AlertTitle>
        <AlertDescription>
          {campaign.status === 'open'
            ? 'Os resultados serão exibidos após o recebimento da primeira resposta.'
            : 'Esta campanha foi encerrada sem respostas.'}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex max-h-[75vh] flex-col gap-6 overflow-y-auto pr-2">
      <NpsResultsSummary campaign={campaign} summary={summaryQuery.data} />

      <Separator />

      <section className="flex flex-col gap-3">
        <div>
          <h3 className="text-base font-semibold">
            Resultados por linha e horário
          </h3>
          <p className="text-sm text-muted-foreground">
            Compare os resultados entre as linhas e os horários avaliados.
          </p>
        </div>
        <NpsResultsSegmentsTabs campaignId={campaign.id} />
      </section>

      {showComments && (
        <>
          <Separator />

          <section className="flex flex-col gap-3">
            <div>
              <h3 className="text-base font-semibold">Comentários</h3>
              <p className="text-sm text-muted-foreground">
                A identificação dos respondentes não é exibida.
              </p>
            </div>
            <NpsResultsComments
              campaignId={campaign.id}
              canViewComments={canViewComments}
            />
          </section>
        </>
      )}
    </div>
  );
};

export default NpsCampaignResultsPanel;
